// ── Feature-modal content registry ─────────────────────────────────────────────────────────────
// One REAL, grounded entry per feature card in the "Explore Vidyapeeth360 Modules" section
// (parts2.js OFFERING_TABS — the operational source families behind the 115-route public catalogue).
// Every `what` paragraph and every capability bullet is
// sourced from docs/product/FEATURE_INVENTORY.md (the code-grounded feature inventory) or CLAUDE.md —
// NO generated pools, NO rotation, NO template phrases. The 16 previously-curated FEATURE_DETAILS
// blurbs are migrated in verbatim as their entry's `what`.
//
// Shape: { what: "2-3 real sentences", capabilities: ["3-4 concrete bullets"], ai?: true }
//  - `ai: true`   → the modal shows the purple Aira-AI chip (only where a real AI capability exists).
// (The old per-feature `art` emoji3d keys + MODULE_ART_KEY were removed 2026-07 — the modal header
//  now reuses the clicked card's own icon, one icon language across card and modal.)
// This file is pure data (no imports) so the coverage check can evaluate it in plain node.

export const FEATURE_CONTENT = {
  // ═══ Academics ═══════════════════════════════════════════════════════════════════════════════
  "Timetable Management": {
    what: "Build each class-section's weekly grid with teacher double-booking detection, workload caps and a dry-run preview before anything saves — or auto-generate a conflict-free draft from subject demands. A substitute board fills periods left open by approved leave, and every teacher gets an iCal feed of their schedule.",
    capabilities: [
      "Catch teacher double-bookings and weekly over-cap warnings BEFORE the grid saves, with a dry-run preview",
      "Auto-generate a conflict-free draft from subject demands, honouring per-teacher and per-room caps",
      "Fill periods left open by approved leave from a ranked substitute board — same-subject teachers first",
      "Give every teacher an iCal feed of their weekly schedule for Google, Apple or Outlook calendars",
    ],
  },
  "Curriculum Planning": {
    what: "Author a chapter-and-topic curriculum tree for every class and subject — the same tree lesson plans link back to, so syllabus coverage is measured against the school's own curriculum, not guesswork. Adopted syllabus text (NCERT or your own) can be stored as grounding material so AI lesson drafting stays true to what you actually teach.",
    capabilities: [
      "Build a per-class, per-subject chapter/topic tree that every lesson plan links back to",
      "See a per-chapter coverage heatmap — a chapter counts as covered only when its plan is completed or HoD-approved",
      "Store adopted syllabus / learning-outcome text as grounding material for AI lesson drafting",
      "Update or replace chapters any time — coverage recomputes live",
    ],
  },
  "Lesson Plan Management": {
    ai: true,
    what: "Plan lessons with objectives, chapters, resources and planned dates, then move each through a real review loop: draft → submitted → approved or returned with the HoD's note. “Draft with AI” writes a first version with 3-5 measurable objectives, grounded on the school's own adopted curriculum — the teacher always reviews before saving.",
    capabilities: [
      "Track planned / in-progress / completed with one-click “mark complete” and a completion % bar",
      "Run an HoD review queue — submit for review, approve, or return with a note",
      "Draft a weekly plan with AI, grounded on your school's own adopted syllabus text",
      "Rewrite any passage to a target reading level (Grade 2-5 / ELL) for mixed-ability classrooms",
    ],
  },
  "Homework & Assignments": {
    ai: true,
    what: "Teachers assign homework with due dates and max marks, then record submission status and grades from an enrollment-backed roster limited to the sections they teach. The current student and parent portals show assignments read-only; they do not provide student self-submission. A teacher can queue a WhatsApp reminder for non-submitters' guardians, subject to provider configuration, consent and opt-outs.",
    capabilities: [
      "Record received / not received status from the teacher's section-scoped roster",
      "Grade recorded submissions with server-clamped marks and feedback",
      "Generate a structured grading rubric (criteria × performance levels, weights summing to total marks) with AI",
      "Queue non-submitter reminders with a per-assignment cooldown and messaging safeguards",
    ],
  },
  "Digital Gradebook": {
    what: "Marks entry runs on the school's board configuration — grade bands, pass marks and scale type (grade points, numeric, or marks-only percentage) come from CBSE/ICSE/state/IB config, never hard-coded. Marks are clamped server-side to each paper's maximum, absences are first-class, and entry locks the moment a term's results are published.",
    capabilities: [
      "Grade on the resolved board scale — switching board re-derives grading everywhere at once",
      "Tag every paper with an NEP/CCE assessment type: formative, summative, project, oral or practical",
      "Roll up a formative-vs-summative CCE view per student across the academic year",
      "Marks lock on publish; reopen for corrections without unpublishing results",
    ],
  },
  "Exam Scheduling": {
    ai: true,
    what: "Plan term exams with CCE assessment types and clash-free datesheets; marks entry is section-scoped, so a teacher can only enter marks for the classes they actually teach. Results flow straight into report cards and CBSE/ICSE/state board export formats.",
    capabilities: [
      "Create term exams per class/section/subject with max marks, pass marks and exam dates",
      "See per-paper statistics — entered, absent, passed, failed, average % — at a glance",
      "Export a term's results in the exact CBSE OASIS / ICSE / state-board upload layout",
      "Get an AI read of one exam's class result — computed from aggregates only, never student names",
    ],
  },
  "Result Publishing": {
    ai: true,
    what: "One click turns entered marks into branded, board-aware report-card PDFs — grading bands, pass marks and NEP HPC domains come from the school's board configuration, never hard-coded. The no-code Report Card Studio lets the school redesign layout, signatories and grading scales itself.",
    capabilities: [
      "Each card is frozen at issue — template, brand palette and scale snapshotted so re-downloads stay byte-identical",
      "Attendance % and NEP HPC co-scholastic grades fold into every card automatically",
      "Draft board-appropriate class-teacher remarks with AI, translated into any of 11 languages",
      "Batch-render a whole class's PDFs into one zip with a signed, expiring download link",
    ],
  },
  "Subject Management": {
    what: "Subjects are a governed per-school picklist that flows everywhere — the timetable grid, teaching assignments, exams, marks and report cards all read the same list, so “Maths” can't fork into three spellings across modules. Sensible India-school defaults (Hindi, EVS, Sanskrit, General Knowledge) ship out of the box.",
    capabilities: [
      "Maintain one governed subject list shared by timetable, exams, homework and report cards",
      "Map subjects to classes through timetable periods and explicit teaching assignments",
      "Substitute ranking uses recorded subject expertise — same-subject teachers surface first",
      "Start from India-school defaults and edit the list freely",
    ],
  },
  "Teacher Mapping": {
    what: "Teaching Assignments is the admin surface that decides exactly which (class, section, subject) each teacher may touch — and it doubles as the security boundary: a classroom teacher's attendance, marks, homework and report-card access is fenced to those sections, enforced server-side. Homeroom flags, bulk assignment and seed-from-timetable are built in.",
    capabilities: [
      "Grant class/section/subject access per teacher, with a single-homeroom-per-section rule",
      "Reassign a leaving teacher's whole load in one operation, or seed grants from the timetable",
      "Per-teacher workload analytics: periods, grants, homerooms, over-cap warnings",
      "Spot coverage gaps — sections with no class teacher assigned",
    ],
  },
  "Syllabus Tracking": {
    what: "Every lesson plan rolls into live syllabus-completion tracking — planned, in-progress and completed counts with a completion % that respects the class, section and subject you're filtered to. The coverage heatmap ties completion back to the curriculum's chapters, so “we finished Chapter 7” is a recorded fact, not a memory.",
    capabilities: [
      "Review current completion % per class, section and subject",
      "A chapter counts as covered only when its plan is completed or HoD-approved",
      "KPI cards keep planned / in-progress / completed visible to coordinators",
      "One-click “mark complete” as the class moves through the term",
    ],
  },
  "Academic Calendar": {
    what: "Run the school year on one calendar — terms, events, exams and holidays — with the academic-year start month resolved from the school's board configuration. Other modules actually read it: bus-trip generation skips holidays, and the annual rollover moves every student up India's class ladder (playgroup → nursery → LKG/UKG → Class 1-12 → graduated).",
    capabilities: [
      "Plan terms, events and holiday lists for the whole year in one place",
      "Board-config-driven academic-year start month — never a hardcoded April or June",
      "Holidays automatically suppress bus-trip generation",
      "Annual rollover runs with a dry-run + checksum approval gate and hold-back support",
    ],
  },
  "Online Classes": {
    what: "Schedule an upcoming class by saving a teacher-provided Zoom, Google Meet or Microsoft Teams join link with its date, time and duration. Vidyapeeth360 organises and opens the external meeting; it does not host the call or create a recording. Sessions sit beside each course's ordered notes, video links, files and other materials.",
    capabilities: [
      "Save external Zoom, Meet or Teams join URLs with durations up to 10 hours",
      "Show upcoming sessions first with a one-tap external Join class link",
      "Attach ordered lessons and materials to every course",
      "Course-360 view aggregates lessons, enrolled students and the mapped teacher",
    ],
  },
  "Study Material": {
    what: "Build courses per class/section and attach ordered materials — notes, videos, links and files with validated URLs — that teachers curate and reorder as the term progresses. The school's own adopted curriculum text can also be stored as grounding, so AI-generated lesson plans and question papers stay on-syllabus.",
    capabilities: [
      "Add note / video / link / file materials in a teacher-controlled order",
      "Reorder lessons any time; the Course-360 overview aggregates everything",
      "Store adopted syllabus text as grounding material for the AI helpers",
      "Module KPIs track total courses, materials and upcoming live classes",
    ],
  },
  "Student Performance": {
    ai: true,
    what: "Every roster row carries cumulative attendance % and academic performance % with a good/watch/at-risk band — computed from the same tenant-wide aggregates the Insights console uses. The Student-AI suite adds a 60-90-word learner snapshot, an explainable risk “why”, and supported questions over the permitted student's records.",
    capabilities: [
      "Performance bands on the roster: attendance %, marks %, good / watch / at-risk",
      "AI learner snapshot + risk explanation grounded in real signals — the platform computes every number, AI only writes the words",
      "A merged growth timeline: observations, reflections, traits, report cards and achievements",
      "Ask questions about one student over attendance/fees/marks — section-scoped, health data firewalled",
    ],
  },
  "Class Management": {
    what: "Classes carry real structure: a homeroom class teacher, per-class seat-capacity caps (enforced at admission, with an authorised override), and India's full class ladder from playgroup to Class 12. Rosters are enrollment-backed, and per-class fee structures, timetables and exam schedules all hang off the class.",
    capabilities: [
      "Per-class capacity caps — admission blocks when full, with an admin override",
      "The homeroom class-teacher link drives attendance access and daily scoping",
      "Per-class fee structures, timetable grids and exam schedules",
      "Bulk promotion up the India class ladder at year end",
    ],
  },
  "Section Management": {
    what: "Divide classes into sections without the guesswork: the allocation aid suggests the least-filled section with live per-section counts against caps, and duplicate roll numbers within a section are blocked. Sections are also the unit of record-level security — a section's teacher sees that section, not the whole school.",
    capabilities: [
      "Least-filled-section suggestion with live counts vs capacity",
      "Per-section seat caps enforced at admission (blocks when full)",
      "Duplicate roll-number-per-section guard on every save",
      "Sections drive teacher scoping, timetable grids and attendance registers",
    ],
  },
  "Certificate Generation": {
    what: "Issue serialized, QR-verifiable, brand-themed PDF certificates — Transfer, Bonafide, Character, Transcript, No-Dues, Fee-Paid, Promotion and staff Experience letters — each with the holder's identity frozen at issue. The Certificate Builder designs custom certificates with {{merge_fields}}, and anyone can verify a certificate by scanning its QR — a revoked one scans invalid.",
    capabilities: [
      "8 typed certificates plus a merge-field design studio for custom ones",
      "Sequential serials (TC/<year>/0001) with a public QR verification page",
      "No-Dues hard-blocks while fees are outstanding; transcripts auto-fill the marks table",
      "Bulk-issue to a whole class and batch-download as a single zip",
    ],
  },
  "Promotion Management": {
    what: "The academic-year rollover promotes every active student one step up the India class ladder in a single transaction — with a dry-run that returns a checksum the execute step must echo back, so nobody bulk-promotes by accident. Hold-back IDs repeat a class in the new year, and graduates flow automatically into the Alumni directory.",
    capabilities: [
      "Dry-run + checksum approval gate before anything moves",
      "Hold back specific students to repeat a class in the new academic year",
      "Graduates auto-create alumni records and fire a student.graduated event",
      "Transaction-atomic — any failure rolls the whole rollover back",
    ],
  },

  // ═══ Administration ══════════════════════════════════════════════════════════════════════════
  "Student Admission": {
    what: "Enrol a student in one form: the admission number is server-minted and sequential (ADM0001…), guardians are captured for WhatsApp updates, and a QR-verifiable ID card can be printed immediately. Class-capacity caps and a section-balance hint keep allocation sensible.",
    capabilities: [
      "Server-minted sequential admission numbers shared by every create path — manual, import, convert, transfer",
      "Class-capacity caps with a least-filled-section allocation hint",
      "Guardians captured for WhatsApp updates; a QR-verifiable ID card prints immediately",
      "CSV bulk import with per-row validation preview — imported batches never message parents",
    ],
  },
  "Enquiry Management": {
    ai: true,
    what: "A drag-drop admissions CRM: enquiry forms on your public website feed the lead pipeline, stages move through a governed workflow, and one click converts an admitted lead into a real student record. OTP-verified self-registration with an admin approval queue absorbs the admission-season rush.",
    capabilities: [
      "6-stage Kanban (New → Contacted → Visit → Application → Admitted/Lost) with live counts and funnel stats",
      "Public enquiry forms capture leads with duplicate detection and auto-assignment",
      "AI drafts warm follow-up messages — PII-safe: only pipeline context ever reaches the model",
      "“Admit as student” converts a lead atomically, carrying uploaded photos and documents across",
    ],
  },
  "Staff Management": {
    what: "When the HR & Payroll workspace is enabled for your school (it's still rolling out school by school), maintain a staff directory with departments, designations, auto-generated employee codes (EMP0001) and format checks for India statutory fields such as PAN, UAN, ESIC and IFSC. Bank-account values use protected storage and masked display, while staff with linked history can be deactivated instead of hard-deleted.",
    capabilities: [
      "Directory CRUD with search, department filters and an Employee-360 detail view",
      "Grant app access in one click — mints a login and delivers credentials over email + WhatsApp",
      "Statutory fields validated server-side: PAN, 12-digit UAN, ESIC, IFSC, Aadhaar-last-4 only",
      "Staff self-service: own profile, payslips and statutory self-completion",
    ],
  },
  "Role & Permission": {
    what: "Access is layered, not flat: role permissions, your plan's included features, tenant isolation and record-level section scoping are all checked on every request — and permission changes take effect immediately, because they resolve fresh per request instead of living in a stale token. Payroll is deliberately split from HR, so an HR clerk can edit the directory but can't touch pay.",
    capabilities: [
      "Module × action permissions per role, with tenant-level role grants",
      "Separation of duties built in: requester ≠ approver on refunds, concessions, leave and payroll",
      "Classroom teachers are record-scoped to only the sections they teach",
      "Permission changes apply instantly — no re-login required",
    ],
  },
  "ID Card Management": {
    what: "Print-ready CR80 ID cards, 2×4 per A4 sheet, with the school band, photo, class, roll and admission number — and a QR that anyone can scan to verify against the live record, so a withdrawn student's card scans invalid. Print one card, a class, or the whole school through an async batch with a signed download link.",
    capabilities: [
      "CR80 print layout with the school's brand accent and header",
      "QR verifies against a public endpoint that reflects live enrolment status",
      "Bulk by class/section; large print jobs render as background batches",
      "Photos embed only from the school's own stored files — never external fetches",
    ],
  },
  "Document Management": {
    what: "A school document library over object storage — 25 MB uploads, a verification workflow, expiry tracking — plus a request → submit → review collection loop: the school defines required document types (Aadhaar, PAN, TC…), portals upload, admins approve. A dashboard shows per-class completeness % and everything expiring in the next 60 days.",
    capabilities: [
      "Required-documents checklist per student and employee, auto-created at onboarding",
      "Verify or reject with owner-module RBAC — employee documents gate on HR, not student staff",
      "A daily expiry sweep raises notifications and a rule-engine event",
      "Portal users only ever see files on their OWN record",
    ],
  },
  "Transport Management": {
    what: "Geocoded routes and stops, a fleet and driver/attendant master with document-expiry alerts, and seat-capacity-aware student allocation with prorated monthly transport fees (GST handled). The live layer adds GPS trip tracking, an append-only boarding safety log and “your child boarded” WhatsApp alerts.",
    capabilities: [
      "Geocoded routes and stops with pickup/drop times and geofence radii",
      "Fleet + crew masters tracking RC, insurance, permit, PUC, fitness and licence expiries",
      "Seat-safe allocation with FIFO waitlists and prorated monthly billing, GST-aware",
      "Live trips: GPS pings with an honest live/stale badge, boarding log, SOS and parent alerts",
    ],
  },
  "Task Management": {
    what: "An always-on school-operations workspace for ad-hoc tasks and reusable SOP templates. Configure a role or named assignee, branch scope, priority, SLA, checklist, daily/weekly/monthly recurrence and optional photo, document or video proof; staff work from My Day, list, board or calendar views while authorised managers use the review queue, comments, evidence PDFs, SOP binders and completion reports. Optional AI proof checking needs Aira on your plan, plus credits and a configured vision provider: unavailable or ambiguous checks stay in manual review, while a sufficiently confident pass can auto-verify only when the template permits it and may be held for a configured human spot-check sample.",
    capabilities: [
      "Ad-hoc tasks and reusable daily, weekly or monthly SOP templates with role/user and branch assignment",
      "Typed checklists, SLA timers, overdue escalation and optional photo, document or video proof",
      "Manual review, optional credit-metered AI proof evaluation, human sampling and corrective follow-up tasks",
      "Status history, comments, CSV exports, evidence PDFs, SOP binders and completion reporting",
    ],
  },
  "Hostel Management": {
    what: "Boarding-house rooms and bed allocation, gate passes for exits, and monthly mess billing that lands on the student's regular fee invoice — in exact paise, never a parallel ledger. Wardens see occupancy at a glance; parents see hostel charges beside tuition in the same portal.",
    capabilities: [
      "Rooms and beds with live occupancy %, vacancies by block, allocate and vacate",
      "Gate-pass lifecycle: request → approve → checkout → checkin, with overdue flags",
      "Guardians notified automatically when a boarder leaves or returns",
      "Monthly mess billing lands on the regular fee invoice, idempotent per period",
    ],
  },
  "Inventory Management": {
    what: "A ledger-integrity store, not a spreadsheet: every stock movement is an append-only ledger row, valuation is perpetual weighted-average cost driven by goods receipts, and store-outs go through an issuance queue where a second person approves, so stock only decrements on approval. Purchase orders receive into stock, post supplier payables, and mirror into the school's books.",
    capabilities: [
      "Append-only movement ledger; deletes archive, never wipe history",
      "Two-person stock issuance — one person requests, a second approves",
      "PO → goods receipt that auto-creates missing items so payable always equals goods received",
      "Physical-count audit with a variance report and one-click reconciliation",
    ],
  },
  "Library Management": {
    what: "Catalogue the collection, issue and return against student and staff records, take reservations, and apply overdue fine rules that flow into the same fee ledger as everything else. Stock and overdue reports are built in.",
    capabilities: [
      "Catalogue with copies tracking; issue and return with configurable loan periods",
      "Reservations with automatic waitlist promotion when a copy returns",
      "Per-day overdue fines (school-configurable rate) tracked through to paid",
      "Book-360 detail: active loans, live accruing fines, recent returns",
    ],
  },
  "Visitor Management": {
    what: "The reception desk logs every walk-in — name, phone, purpose, whom to meet, ID proof — with check-in, pre-registration for expected visitors, and check-out. A live “on premises for 47 min” duration shows who's still inside, and ID capture is DPDP-minimal: only the last 4 digits of an Aadhaar, PAN or licence are ever stored.",
    capabilities: [
      "Check in now, pre-register expected visitors, check out with a final duration",
      "Live on-premises view — exactly who is inside right now",
      "India-KYC ID proof types with last-4-digits-only storage (DPDP)",
      "Visitor-360: host resolution, past visits and linked gate passes",
    ],
  },
  "Front Office": {
    what: "Reception's daily cockpit: the visitor log, QR gate passes with an approval step, a walk-in/phone enquiry register, and a complaints book — with KPIs for on-premises-now, open enquiries and pending passes. One click converts an enquiry into an admissions CRM lead, so a walk-in never leaks out of the funnel.",
    capabilities: [
      "Gate passes mint scannable QR codes and require approval before they're valid",
      "Enquiry register with an idempotent one-click convert-to-lead",
      "Complaints move open → in-progress → resolved, and resolving requires a recorded resolution",
      "Dashboard KPIs across visitors, passes, enquiries and complaints",
    ],
  },
  "Asset Management": {
    what: "A fixed-asset register with per-asset depreciation — SLM or WDV, useful life, salvage value, rate — computed paise-exact into book value and accumulated depreciation. Running depreciation posts the incremental charge into the school's books (idempotent per period), and disposing an asset writes its accumulated depreciation back so the balance sheet is never stranded.",
    capabilities: [
      "Asset register with tags, categories, locations and in-use / idle / disposed status",
      "SLM and WDV depreciation with a whole-portfolio register",
      "“Run depreciation” posts the charge into the GL, idempotent per month",
      "Disposal write-back keeps the Balance Sheet clean",
    ],
  },
  "Branch Management": {
    what: "A trust or chain running several schools gets one group console: a cross-school roster with per-school KPIs (students, fee outstanding, plan), a group-wide roll-up, and one consolidated billing statement per month. Students transfer between member schools through a guardian-consent-gated request that carries their APAAR/UDISE identity — and one group can never see another's data.",
    capabilities: [
      "Cross-school comparison table plus group-wide roll-up KPIs",
      "Consolidated monthly billing statement with per-school lines and CSV",
      "Consent-gated student transfers that keep one identity across campuses",
      "A group-wide white-label brand every campus inherits, with per-school overrides",
    ],
  },
  "User Management": {
    what: "Staff logins are provisioned, never self-signed-up: the admin creates a user and gets a handover bundle — school code, sign-in link, and a unique random temporary password with a forced change on first sign-in. Permissions resolve fresh on every request, mandatory 2FA guards every login, and the first successful sign-in flips an invite to active.",
    capabilities: [
      "One-click staff invite with credentials delivered over email + WhatsApp",
      "Unique random temp passwords and a forced first-login password change",
      "Mandatory OTP 2FA, or an offline authenticator (TOTP) with backup codes",
      "Login history is immutable at the database level",
    ],
  },
  "Institute Settings": {
    what: "The school's control room: profile with the shareable School Code (plus a join-page QR), board selection that re-derives grading everywhere in one transaction — board is configuration, never code — academic year, branding, and GST details verified by the official checksum and a live registry lookup. Sensitive settings only change through their own audited endpoints.",
    capabilities: [
      "School Code with availability check, copy button and a join-page QR",
      "Switching board re-derives report-card and exam grading in one step",
      "GSTIN validated by the official mod-36 check digit, with live registry verification",
      "An audited “Go live” switch lifts the onboarding quiet-mode when you're ready",
    ],
  },
  "Approval Workflow": {
    what: "A second person approves — that is the house rule wherever money or records move: expense vouchers post to the books only after a second person approves, refunds and concessions reject self-approval, leave can't be approved by its own applicant, stock issues need sign-off, and registration requests run approve / reject / ask-correction loops. Every decision is audited.",
    capabilities: [
      "Pending → approved/rejected flows on expenses, refunds, concessions, leave, stock and payouts",
      "Self-approval blocked everywhere unless the approver holds full admin rights",
      "Field-scoped ask-correction loop on registration requests",
      "An immutable audit trail behind every decision",
    ],
  },
  "Custom Forms": {
    what: "Salesforce-grade extensibility rare in school ERPs: build dynamic forms (8 field types, conditional show/hide logic enforced server-side, an anonymous-by-construction option), add governed custom fields to the student record with PII flags and role visibility, and even create whole custom objects — Events, Assets, Clubs — with their own records.",
    capabilities: [
      "Form builder with drafts, publish windows, single-response dedupe and CSV export",
      "Conditional show-if logic — hidden fields drop their values server-side",
      "Custom fields with a PII flag for supported AI-context exclusion and per-role visibility",
      "Tenant-created custom objects with a drag-drop page-layout builder",
    ],
  },

  // ═══ Fees & Finance ══════════════════════════════════════════════════════════════════════════
  "Fee Structure": {
    what: "Define per-class fee structures with named components — each carrying an amount in exact paise, an HSN/SAC code and a GST rate — on a monthly, quarterly, term or annual frequency. Structures archive rather than delete, so history behind issued invoices is never broken, and opt-in demand generation auto-bills each period through the same golden invoice path.",
    capabilities: [
      "Named components with HSN/SAC + GST rate per head — tuition exempt, transport/hostel taxable",
      "Monthly, quarterly, term and annual billing frequencies",
      "Bulk invoice generation across a class with skip-existing dedup",
      "Opt-in automatic demand generation, idempotent per billing period",
    ],
  },
  "Fee Collection": {
    what: "Record counter cash, UPI, cheque and card payments, or create online orders through the school's configured Razorpay or Cashfree account. A payment receives a sequential receipt and updates its invoice only after the relevant counter evidence or verified provider callback. Final bank credit, fees and deductions remain provider or bank evidence for the school to reconcile.",
    capabilities: [
      "Atomic payment recording — clamped to the outstanding due, idempotent, receipt-numbered",
      "Cash, UPI, cheque, card and provider-confirmed gateway methods in one invoice ledger",
      "Overpayments can ledger into a student advance/credit balance and apply to later invoices",
      "Provider callbacks update the invoice; provider or bank evidence confirms final settlement",
    ],
  },
  "Online Payment": {
    what: "Parents pay by UPI, card or netbanking with no login — a Pay Now link and QR work straight from a WhatsApp reminder. Amounts are server-derived and clamped to the invoice's outstanding due, so partial payment is allowed and over-charging is impossible.",
    capabilities: [
      "No-login Pay Now links and QR codes straight from a WhatsApp reminder",
      "Server-derived amounts clamped to the invoice's outstanding due",
      "“Paid” means webhook-confirmed by the gateway — never a closed browser tab",
      "UPI direct to the school's own VPA, with staff-verified UTR + screenshot proof",
    ],
  },
  "Pending Fee Tracking": {
    what: "Dues are visible everywhere: KPI tiles for pending and overdue, a days-overdue badge on every invoice row, one-click status filters, and a defaulters roll-up per student — count, total due, oldest due date. Invoices flip due → overdue automatically past their date, so the numbers stay honest without anyone doing it by hand.",
    capabilities: [
      "Automatic due → overdue flip past the due date",
      "Defaulter roll-up with an optional fee-block that gates certificate issuance",
      "Fee-risk insights flag families with repeated late payments",
      "Bulk-select overdue invoices and send reminders in one action",
    ],
  },
  "Discount & Scholarship": {
    what: "Waivers are governed money, not a quiet edit: staff request a concession on an invoice, a different person approves, and approval posts a negative line item clamped to the due — idempotent and audited. A concession master (sibling, staff-ward, category, merit) standardises schemes, and awarded scholarships push onto invoices the same way, oldest dues first.",
    capabilities: [
      "Concessions need a second approver — the requester can never self-approve",
      "Standing schemes: sibling, staff-ward, category, merit or custom; percent or flat",
      "Scholarship engine: schemes → applications → approve/award → apply to fees",
      "Caps enforced server-side; every rupee reconciles the invoice ledger",
    ],
  },
  "Fine Management": {
    what: "Configure flat-rupee or percentage late-fee rules with grace days, a cap and an optional fee-head scope. Staff preview the affected overdue invoices and apply a rule on demand; the current workflow does not claim unattended scheduled application. Each rule is idempotent per invoice so a repeated apply request does not add the same fine twice.",
    capabilities: [
      "Flat or percent fines with a grace period and cap",
      "Dry-run preview before staff choose to post the fine",
      "On-demand application, idempotent per rule per invoice",
      "Period-close-guarded like every other money mutation",
    ],
  },
  "Receipt Generation": {
    what: "Every payment mints a sequential receipt number (RCP0001…) from one atomic per-school register — gap-free on rollback, never burned by a webhook retry — and renders a branded PDF shared over WhatsApp and email at pay time. Re-download any past receipt with a DUPLICATE watermark; the Receipt Builder customises masthead, signatories and declarations.",
    capabilities: [
      "Sequential, race-safe receipt numbering shared by counter and gateway payments",
      "Branded PDF at pay time; every reprint carries a DUPLICATE watermark",
      "Receipt Builder: masthead, footer, accent, logo, 1-4 signatories, field toggles",
      "Optional CGST/SGST breakup computed from each head's HSN/SAC rate",
    ],
  },
  "Installment Setup": {
    what: "Split any invoice's outstanding due into dated installments — equal-split or explicit amounts that must sum to the due. Each installment's covered/partial/overdue state is derived from the invoice's cumulative payments, so the invoice remains the single source of truth and a payment plan can never disagree with the ledger.",
    capabilities: [
      "N dated installments, auto-split or explicit, validated to sum to the due",
      "Status derived from real payments — never a parallel ledger",
      "Visible in the invoice drawer beside the activity timeline",
      "Soft-cancel a plan without touching the money",
    ],
  },
  "Expense Management": {
    ai: true,
    what: "Expense vouchers are created pending and post to the books only after a second person approves — self-approval is blocked, approved vouchers become immutable, and every voucher gets a sequential number. AI suggests the right ledger category from the payee and note (grounded only on the school's own category list), and duplicate-payment detection flags the same payee + amount within 14 days.",
    capabilities: [
      "Second-person-approved vouchers with category breakdowns and CSV export",
      "AI category suggestion, plus median-based outlier and duplicate detection",
      "Budgets per period and category, with variance that ties to the Income & Expenditure",
      "A books-close lock: nothing posts into a closed period",
    ],
  },
  "Payroll": {
    what: "When the HR & Payroll workspace is enabled for your school (it's still rolling out school by school), run monthly payroll with configured §192 TDS calculations, EPF and ESI handling, ECR/ESIC exports, Form-16 worksheets and payslip PDFs. Bank-disbursal batches need a second approver, capture payment evidence and can post paid salary results into the school's ledger.",
    capabilities: [
      "§192 TDS with a true-up over the remaining FY months; EPF/ESI with a per-school opt-out",
      "EPFO ECR v2 and ESIC challan exports that tie to the salary register",
      "Bank disbursal through payout batches a second person approves, with per-employee UTRs captured",
      "Salaries post gross to the books, with withheld PF/ESI/TDS recognised as a statutory liability",
    ],
  },
  "Accounting Reports": {
    what: "A real management GL derived directly from the fee, payment, expense and journal ledgers — Balance Sheet (balanced by construction), Income & Expenditure, Trial Balance, Day Sheet — plus named registers like Parties, Petty Cash and Ledger Head that reconcile to the statements. Refunds and reversals net correctly everywhere, and Tally XML export drops straight into your CA's workflow.",
    capabilities: [
      "Classified statements without hand-journalling a single transaction",
      "Hierarchical India-school chart of accounts with rolled-up balances",
      "Tally ERP XML export of receipts, payments, refunds and journals",
      "CSV export on every statement and register",
    ],
  },
  "Payment Gateway": {
    what: "Bring your own gateway: the school connects its own Razorpay or Cashfree account, with protected credentials and a verification probe before live collection. Vidyapeeth360 creates invoice-bound orders and verifies callbacks; it does not intentionally pool school fee funds. The provider controls settlement timing and deductions, so final bank credit remains a reconciliation step for the school.",
    capabilities: [
      "School-owned provider account; the platform does not intentionally pool school fee funds",
      "Credentials envelope-encrypted; a sandbox config can never hit the production host",
      "Webhook-confirmed payments with a hash-chained, append-only audit log",
      "Provider and bank evidence remains the source for settlement, deductions and final credit",
    ],
  },
  "Refund Management": {
    what: "Refunds go back to the source payment with a second approver — the requester can't approve their own — with the refundable balance validated under a row lock so over-refunding is impossible. Online refunds are gateway-confirmed before the fee ledger reverses; a failed refund releases the reserved amount, and the parent is notified when it lands.",
    capabilities: [
      "Full or partial refunds, validated against the refundable balance",
      "The ledger reverses only on a gateway-confirmed verdict",
      "Append-only corrections: void an invoice or reverse a bounced payment, credit-note style",
      "Every state change lands on the invoice's reconciliation timeline",
    ],
  },
  "Fee Reminder": {
    ai: true,
    what: "Aira's fee-recovery engine scores eligible overdue accounts, drafts a reminder in any of 11 supported languages, and can escalate tone stage by stage — reminder, follow-up, firm, final, then a call task for the office. Each configured run re-checks invoice state and removes confirmed paid invoices from the active ladder.",
    capabilities: [
      "Pay-propensity scoring escalates tone and channel over a 5-rung ladder",
      "AI drafts in 11 languages; the fee desk edits and sends verbatim",
      "Paid-state recheck and cooldown before each configured reminder",
      "Runs halt automatically the moment the gateway confirms payment",
    ],
  },
  "Category-wise Fees": {
    what: "Price by cohort, not one-size-fits-all: per-class fee structures, route-based transport fees, room-wise hostel heads, and concession schemes for sibling, staff-ward, category (SC/ST/OBC/EWS) and merit cases. Every head can carry its own GST treatment and even map to its own income account in the books.",
    capabilities: [
      "Per-class and per-group structures with distinct fee heads",
      "Sibling / staff-ward / category / merit concession schemes",
      "Per-fee-head GL mapping allocates payments proportionally, paise-exact",
      "RTE/EWS quota reporting sits alongside in the student system",
    ],
  },
  "Transport Fees": {
    what: "Route fees become real invoices through a monthly billing run — one transport invoice per rider, idempotent per month, with proration built in (join on the 1st: full month; by the 15th: half; later: skipped). GST is a per-school choice (exempt for own fleets, or the 5/12/18% slabs for operators), and transport income lands in its own ledger head, never mixed into tuition.",
    capabilities: [
      "Monthly billing run with preview-before-commit and per-student proration",
      "Waitlisted or suspended riders never get billed",
      "Per-school GST configuration on SAC 9964, split computed on read",
      "Reconciles to “Transport Fees Income” across every statement",
    ],
  },
  "Hostel Fees": {
    what: "Monthly mess fees bill every active boarder as a real fee-invoice line through the same golden invoice path as tuition — exact paise, idempotent per period, already-billed residents skipped automatically. Parents see hostel charges beside tuition in the same portal and pay them the same way.",
    capabilities: [
      "One-click monthly mess billing across all active boarders",
      "Idempotent per period — a re-run never double-bills anyone",
      "Hostel fee heads carry their own GST rate",
      "The same receipts, reminders and reconciliation as every other fee",
    ],
  },
  "Financial Summary": {
    ai: true,
    what: "A live money picture in KPIs that exclude voided noise: billed, collected, pending, overdue and collection rate, broken down by class and by payment method. The AI layer adds a weekly cash-flow forecast built on the school's own historical collection rate, and “talk to your books” answers plain-English finance questions grounded only on PII-safe aggregates.",
    capabilities: [
      "Collections KPIs with by-class and by-method breakdowns",
      "A weekly cash-flow forecast with shortfall flags, computed from your own figures — no AI guessing",
      "Plain-English finance Q&A — no student or payee names ever reach the model",
      "Bank-statement ↔ ledger reconciliation with matched and unmatched lists",
    ],
  },

  // ═══ Attendance ══════════════════════════════════════════════════════════════════════════════
  "Student Attendance": {
    what: "Use keyboard-first marking, “all present” with one-tap undo, and period-wise or whole-day registers that roll up cleanly. When an absence-alert provider is configured, saving can queue an eligible guardian WhatsApp alert through the consent-aware funnel. A scheduled scan can flag students below the board-configured attendance threshold, and the CBSE Form-3 register is available for review.",
    capabilities: [
      "Keyboard-first marking (1-4, arrow keys) and “all present” with one-tap undo",
      "Whole-day or per-period registers with a clean day roll-up rule",
      "Configured absence alerts queue on save with consent, opt-out and quiet-mode checks",
      "Re-marking keeps a per-cell before/after audit diff — nothing is silently overwritten",
    ],
  },
  "Staff Attendance": {
    what: "When the HR & Payroll workspace is enabled for your school (it's still rolling out school by school), review employee-code punch logs alongside approved leave and substitute coverage. Employee punches remain presence evidence: they do not create payroll-ready staff attendance or feed salary calculations automatically. The substitute board can rank available same-subject teachers for uncovered periods.",
    capabilities: [
      "Employee-code punches recorded in a filterable evidence log",
      "An “on leave today” summary computed from approved leave",
      "The substitute board fills an absent teacher's periods in one click",
      "Punch logs do not automatically create payroll attendance or salary inputs",
    ],
  },
  "Biometric Attendance": {
    what: "A compatible reader or school-supplied device middleware can post punches to a per-device token webhook. A matched student's IN punch can mark that student present without overwriting a teacher's existing manual register; OUT punches remain in the punch log. Vidyapeeth360 provides the generic bridge, not a bundled vendor-specific biometric SDK.",
    capabilities: [
      "Per-device ingest tokens — rotate to instantly revoke a compromised device",
      "A matched student's IN punch can mark present without replacing an existing manual mark",
      "Unmatched punches are held for review, never silently dropped",
      "Compatible device middleware is required; vendor SDK integration is not bundled",
    ],
  },
  "QR Attendance": {
    what: "Every Vidyapeeth360 ID card carries a QR code, and a compatible scan flow can submit its admission-number payload to the device bridge — a matched scan marks the student present idempotently, without overwriting a teacher's manual register. The same QR can verify the card against the live student record.",
    capabilities: [
      "A QR on every ID card, scannable by any commodity reader",
      "Scan → punch → present: idempotent and rate-limited",
      "Rides the same per-device token webhook as biometric devices",
      "A withdrawn student's card scans invalid on the public verify page",
    ],
  },
  "RFID Attendance": {
    what: "RFID card taps post to the same provider-agnostic device webhook — each reader gets its own ingest token (rotate to revoke), punches resolve tenant-scoped to admission numbers, and a matched “in” tap records a present mark. Hostel gate passes accept an RFID tag at check-out and check-in too, so a boarder's movements reconcile.",
    capabilities: [
      "Card-tap logging with per-device tokens and rate limiting",
      "Tap → present, never overwriting a teacher's manual mark",
      "Hostel gate-pass check-out/in can record the punch tag",
      "Device summary: punches today, matched, unmatched, marked present",
    ],
  },
  "Leave Management": {
    what: "When the HR & Payroll workspace is enabled for your school (it's still rolling out school by school), staff apply for leave against typed quotas and approvers review the request through the role hierarchy. Balances compute per type for the year, and approved leave can feed the substitute board and an iCal-compatible calendar subscription.",
    capabilities: [
      "Leave types with annual quotas; apply with a half-day flag and contact-during-leave",
      "Second-person approval with notifications in both directions",
      "Per-type balances plus a school summary including on-leave-today",
      "Personal and team .ics calendar feeds with a webcal subscribe URL",
    ],
  },
  "Holiday Management": {
    what: "Configure the school's working days and holiday lists once for the connected scheduling workflows: the timetable grid knows the working week, bus-trip generation skips configured holidays, and the academic calendar carries terms, events and vacations for authorised users to review.",
    capabilities: [
      "Holiday lists plus working-day configuration in one place",
      "Bus-trip generation skips dates on the configured holiday list",
      "Terms, events and vacations on one shared academic calendar",
      "Academic-year boundaries resolve from the school's board configuration",
    ],
  },
  "Late Entry Tracking": {
    what: "Late is a first-class status, not a note in the margin: mark students Late in the same keyboard-fast roll-call, see it separated in the status-breakdown analytics, and keep the audit diff when a Late is later corrected. Device punches carry timestamps, so the school can see exactly when a student actually arrived.",
    capabilities: [
      "Late as a distinct status across registers, month grids and analytics",
      "Correction-aware re-marking with a before/after audit trail",
      "Timestamped device punches show true arrival times",
      "The status-breakdown chart separates late from absent",
    ],
  },
  "Daily Attendance Report": {
    what: "Each day rolls into KPI tiles — the latest-day rate and a rolling 7-day average with its delta against the prior week — plus per-class rate bars and a click-through watchlist of students under 75%. The CBSE Form-3 shortfall register is one download away, filterable by class and section.",
    capabilities: [
      "Day summaries with present-rate, per-class bars and trend charts",
      "Chronic (<60%) and at-risk (60-75%) counts with click-through to the students",
      "CBSE Form-3 attendance-shortfall register as CSV",
      "One-click parent alerting straight from the shortfall list",
    ],
  },
  "Monthly Attendance Report": {
    what: "A student-by-day matrix for any class, section and month with per-student rates — multi-period days roll up by the best-status rule, or drill into a single period's register. An opt-in monthly digest WhatsApps every guardian a “present X of Y days” summary on the 1st, with quiet-mode and opt-outs honoured.",
    capabilities: [
      "Month grid with a sticky name column and per-student %",
      "Whole-day roll-up or a single period's own register",
      "Opt-in monthly parent digest over WhatsApp",
      "Feeds the cumulative shortfall scan against the board threshold",
    ],
  },
  "Parent Notification": {
    what: "When absence alerts and a supported provider are configured, saving a register queues a WhatsApp alert for each eligible absent student's guardian. Consent, opt-outs, quiet mode and a one-alert-per-student-per-day cap are applied before send; the log records the delivery status returned by the provider.",
    capabilities: [
      "Automatic absence alerts on save, damped to one per student per day",
      "Consent, STOP/START opt-outs and quiet-mode enforced at one send funnel",
      "Provider-returned delivery statuses logged per message",
      "Optional biometric gate alerts on campus entry and exit (default off)",
    ],
  },
  "Period Attendance": {
    what: "Whole-day and period-wise registers coexist: any timetable period (1-12) can keep its own register beside the day register, and the day rolls up as present-if-present-in-any-period. Subject teachers mark their own period, and absence alerts stay damped to one per student per day.",
    capabilities: [
      "Per-period registers living alongside the whole-day register",
      "A clear, fixed day roll-up rule — present in any period counts the day",
      "Cross-period alert damping: one parent alert per student per day",
      "The month grid can show a single period's register",
    ],
  },
  "Attendance Analytics": {
    what: "An insights dashboard for rolling 7-day trends against the prior week, chronic and at-risk counts, and per-class comparisons. A scheduled daily scan checks students with at least 10 marked days against the school's board-configured threshold, with at most one shortfall alert per student per month.",
    capabilities: [
      "Trend chart, status breakdown and per-class rate bars",
      "An at-risk watchlist (<75%) with click-through to the students",
      "A daily cumulative shortfall scan driven by the board threshold — never a hardcoded 75%",
      "Attendance is the heaviest-weighted signal in the student risk model",
    ],
  },
  "Auto Absence Alert": {
    what: "Alerts fire themselves: absence WhatsApps on register save, threshold-shortfall flags from the daily scheduled scan (minimum 10 marked days, idempotent at one alert per student per month), and rule-engine hooks so a school can automate its own escalations — all through the consent-respecting funnel.",
    capabilities: [
      "Absence alerts on save; shortfall alerts from a daily scheduled scan",
      "Thresholds come from the school's board configuration",
      "Idempotent by design: one shortfall alert per student per month",
      "attendance.recorded events feed the school's own automation rules",
    ],
  },
  "Staff Shift Tracking": {
    what: "When the HR & Payroll workspace is enabled for your school (it's still rolling out school by school), workforce schedules define staff shift windows and attendance policies, while workforce time retains punch evidence and exceptions for authorised review. Substitute coverage and workload views support staffing decisions separately; device punches do not become payroll attendance automatically.",
    capabilities: [
      "Configure workforce schedules and the shift windows staff are expected to follow",
      "Review punch evidence, missing-punch exceptions and approved leave in Workforce Time",
      "Coordinate substitute coverage for approved absence without rewriting the timetable",
      "See workload and coverage views separately from payroll processing",
    ],
  },

  // ═══ Reports & Analytics ═════════════════════════════════════════════════════════════════════
  "Student Reports": {
    what: "Canonical SIS reports run tenant-scoped from one catalogue: students by class (boys/girls), by gender, an enrolment trend with monthly and cumulative lines, plus RTE quota-fill and CWSN statutory rosters. The roster itself exports as a filter-aware CSV that omits special-category fields and neutralises spreadsheet-formula injection.",
    capabilities: [
      "Students by class and gender, plus an enrolment trend with charts",
      "RTE 25%-quota fill and CWSN UDISE+ detail reports",
      "Roster CSV export, audited as data egress and capped at 10k rows",
      "Every report paginates and exports as CSV or Excel",
    ],
  },
  "Fee Reports": {
    what: "The finance picture in named reports: fee collection per class (billed, collected, pending), outstanding, aging buckets (0-30 / 31-60 / 61-90 / 90+ days) and a student-level defaulter list — all in exact paise, IST-bucketed so a late-evening payment lands on the right school day.",
    capabilities: [
      "Collection, outstanding, aging and defaulter reports",
      "IST day-bucketing on every money date",
      "CSV and Excel export with numeric rupee columns",
      "Figures reconcile with the GL statements",
    ],
  },
  "Attendance Reports": {
    what: "Attendance summary per class-section with board-threshold flagging — sections under the school's configured minimum are marked, not just listed — plus day summaries, month grids and the CBSE Form-3 shortfall register with CSV download and one-click parent alerting.",
    capabilities: [
      "Per-class-section summaries with board-threshold flags",
      "CBSE Form-3 shortfall register with CSV and notify",
      "6-month attendance trend charts",
      "Transport route-attendance reconciles expected vs actually-boarded riders",
    ],
  },
  "Academic Reports": {
    what: "Exam performance by subject — average % and pass rate — plus the CCE formative-vs-summative rollup per student and class results with rank. Because grading resolves from the school's board configuration, a CBSE school's report and a state-board school's report each follow their own scale automatically.",
    capabilities: [
      "Subject-level average % and pass-rate reporting",
      "The CCE assessment-type rollup across the academic year",
      "Class results with per-student rank",
      "Board-aware grading everywhere — board is config, never code",
    ],
  },
  "Exam Reports": {
    ai: true,
    what: "Per-exam statistics (entered, absent, passed, failed, average %), class results with rank, and term exports in the exact layout each board's portal expects — CBSE OASIS with affiliation and per-subject columns, ICSE/CISCE, or a generic state format. An AI analysis reads one exam's aggregates and suggests where to focus.",
    capabilities: [
      "Exam-360 statistics and class rankings",
      "CBSE OASIS / ICSE / state board-portal CSV exports",
      "Weak-subject focus lists per class, computed straight from the marks",
      "AI exam analysis over aggregates only — student names never reach the model",
    ],
  },
  "Admission Reports": {
    what: "The admissions funnel as data: stage-by-stage conversion (New → Contacted → Visit → Application → Admitted), by-source breakdowns, conversion % and open-vs-admitted counts — so a principal knows which channels actually fill seats and where enquiries stall.",
    capabilities: [
      "Funnel conversion by stage with drop-off visibility",
      "By-source lead breakdowns and conversion %",
      "KPI cards live on the admissions board itself",
      "Exports like every other catalogue report",
    ],
  },
  "Staff Reports": {
    what: "When HR & Payroll and the reports catalogue are enabled, review staff headcount by department, leave summaries, teaching-load roll-ups and payroll-run registers. Payroll snapshots can show gross, deductions, net and headcount, while payout registers retain the recorded payment evidence.",
    capabilities: [
      "Headcount and monthly payroll cost by department",
      "Payroll-run registers that tie to the EPFO ECR filing",
      "Leave summary: pending, approved, on-leave-today",
      "Teacher-360 rollups: teaching load, marking rate, per-section performance",
    ],
  },
  "Transport Reports": {
    what: "Route attendance reconciles who was expected against who actually boarded, per trip — surfacing no-shows and unexpected boarders daily. Route-360 shows utilization and seats left; the bus manifest CSV (stop, student, guardian phone) is gated behind an explicit export permission because PII rides that file.",
    capabilities: [
      "Expected-vs-boarded reconciliation per bus trip",
      "Route utilization and live seat availability",
      "Manifest CSV gated as an explicit data-egress permission",
      "Fleet and crew compliance-expiry work-lists",
    ],
  },
  "Library Reports": {
    what: "Library KPIs at a glance — titles, copies, currently issued, overdue count, fines outstanding vs collected, reservations waiting — with live accrued fines shown on open overdue loans. Book-360 shows a title's loan history and availability in one view.",
    capabilities: [
      "Issue, return and overdue tracking with live fine accrual",
      "Fines outstanding vs collected in the module summary",
      "Reservation-queue visibility with waitlist positions",
      "Category facets computed across the whole catalogue",
    ],
  },
  "Custom Reports": {
    what: "Build a grouped roll-up from one of two supported sources: Students or Fees/Invoices. Choose from allow-listed dimensions such as class, section, gender, status and academic year, then add supported measures such as count, billed, collected or outstanding. This is a constrained report builder, not an arbitrary cross-module query surface.",
    capabilities: [
      "Point-and-click dimensions × measures roll-ups",
      "Allow-listed combinations only — zero query-injection surface",
      "Client-side CSV with formula-injection neutralisation",
      "Money measures kept in exact paise, rendered as ₹",
    ],
  },
  "Dashboard Analytics": {
    ai: true,
    what: "Role-aware dashboards land each person in their daily workspace with current KPIs. Leaders can review a morning briefing covering attendance, at-risk counts, fee outstanding, collection rate and open tasks, narrated by AI when enabled — and the numbers still show if the AI is off.",
    capabilities: [
      "Current KPI tiles across students, fees, attendance and tasks",
      "The head's morning briefing — the platform computes the numbers, AI writes only the prose",
      "Per-persona home routes: accountants land on Fees, teachers on Attendance",
      "Theme-aware, accessible charts with screen-reader data tables",
    ],
  },
  "Performance Insights": {
    ai: true,
    what: "An explainable early-warning system, not a black box: every at-risk student gets a 0-100 score across four drivers — attendance level, attendance momentum, academics, fee stress — with each driver's exact point contribution shown, so a teacher sees WHY. One click turns a flag into a tracked intervention task seeded with a named playbook.",
    capabilities: [
      "Multi-factor at-risk ranking with per-driver explanations",
      "A fee-payment risk list wired into the AI fee-recovery console",
      "Four intervention playbooks that become SLA'd follow-up tasks",
      "Computed without AI by design — no student data ever leaves the process",
    ],
  },
  "Export PDF": {
    what: "Generate supported operational documents as branded PDFs: board-aware report cards, numbered fee receipts, payslips, QR ID cards and serialized certificates. Each document type applies its own issuance, reprint and permission rules; this is not a universal PDF export for every analytics report.",
    capabilities: [
      "Report cards, receipts, payslips, ID cards and certificates as branded PDFs",
      "Whole-class batches render asynchronously into a zip with a signed, expiring link",
      "Devanagari and Odia names render correctly via script-aware fonts",
      "DUPLICATE watermarks on receipt reprints; REVOKED stamps on revoked certificates",
    ],
  },
  "Export Excel": {
    what: "Every catalogue report streams as CSV or real Excel (numeric rupee columns via openpyxl), honouring the exact filters on screen — and every export writes an audit row, because a data download is data egress. Fees, students, alumni, certificates and the finance registers all have their own permission-gated exports.",
    capabilities: [
      "CSV + XLSX on all thirteen catalogue reports",
      "Exports honour the on-screen filters and parameters",
      "Audited and permission-gated per module (reports:export, students:export…)",
      "Formula-injection-safe cell encoding throughout",
    ],
  },
  "Real-time Charts": {
    what: "Current-data area, bar and donut charts present request-time aggregates from supported dashboards. Chart components can adapt to dark mode and reduced-motion preferences, with accessible labels or companion data tables where the surface provides them. KPI deltas compare defined periods such as this week and last week.",
    capabilities: [
      "Area / bar / donut charts across attendance, fees and enrolment",
      "Dark-mode aware, with reduced motion honoured",
      "Accessible labels and companion data tables on supported chart surfaces",
      "Request-time aggregates instead of a claimed real-time event stream",
    ],
  },
  "Branch-wise Reports": {
    what: "Compare campuses on real numbers: the group console rolls every member school into one table — students, fee outstanding, plan — plus a group-wide summary and one consolidated monthly billing statement with per-school lines and CSV. Inside a single school, the branch-wise finance register breaks billed, collected and outstanding down per class.",
    capabilities: [
      "A cross-school KPI comparison table for trusts and chains",
      "Consolidated monthly group billing statement with CSV",
      "A per-class billed/collected/outstanding register within one school",
      "Group data isolation enforced server-side — one group never sees another",
    ],
  },
  "Daily Summary": {
    ai: true,
    what: "The head's morning briefing compiles what needs attention today — attendance %, newly at-risk students, fee defaulters and outstanding, collection rate, open tasks — from live platform-computed numbers, narrated into a warm, prioritised “do this today” note by AI when enabled. The finance Day Sheet closes each day's cash and bank position.",
    capabilities: [
      "A “what needs you today” digest on the leader's dashboard",
      "AI narration is optional — the numbers still work if the AI is off, and figures can never be invented",
      "The Day Sheet: a daily cash and bank register",
      "Every number reuses the same scoring functions as the consoles, so surfaces never disagree",
    ],
  },
  "MIS Reports": {
    ai: true,
    what: "Thirteen canonical MIS reports in one catalogue — enrolment, attendance, fees, exams, admissions, HR, transport, certificates — each returning a uniform table + KPI shape with date and academic-year parameters. Ask in plain English and the AI routes your question to the right report, runs the safe parameterized query, and explains the answer — it never writes SQL.",
    capabilities: [
      "13 pre-built reports grouped by area, paginated and exportable",
      "“Ask Aira” natural-language routing to the best-matching report",
      "AI narration grounded only on a PII-scrubbed aggregate digest",
      "An extensible registry — new reports join the same catalogue",
    ],
  },

  // ═══ Communication ═══════════════════════════════════════════════════════════════════════════
  "SMS Alerts": {
    what: "Transactional SMS through India's DLT regime done right: MSG91 Flow-based sends with a registered sender ID and a per-message-type DLT template ID — one template is never reused for every body, which is exactly what gets schools blocked. Delivery statuses flow back into one unified log with retry and dead-letter handling.",
    capabilities: [
      "MSG91 SMS with a DLT-registered sender and per-type template IDs",
      "Exponential-backoff retries plus an operator “retry failed” sweep",
      "STOP/START opt-outs honoured at the single send funnel",
      "A per-message delivery-event timeline in the log",
    ],
  },
  "WhatsApp Alerts": {
    what: "Business WhatsApp the compliant way: Meta-approved templates through the school's own BSP (MSG91 or WhatsApp Cloud API), consent and STOP/START opt-outs honoured, and every message's delivery status logged. Absence alerts, fee reminders, results and notices all ride this one funnel.",
    capabilities: [
      "Meta-approved template sends — never free text outside the 24-hour window",
      "Per-school WABA credentials with signature-verified webhooks",
      "Template sync from your WABA plus a picker in the composer",
      "Delivery and read statuses with no-downgrade ordering",
    ],
  },
  "Email Notifications": {
    what: "Send school email through configured SendGrid, SMTP or sandbox delivery, with layered configuration, protected credentials and the school's display name in the From line. Non-transactional mail can carry one-click unsubscribe, while transactional messages follow their own policy. Provider acceptance or a send event does not guarantee inbox delivery.",
    capabilities: [
      "SendGrid or SMTP with per-school overrides and encrypted secrets",
      "One-click unsubscribe (List-Unsubscribe) on bulk mail",
      "Broadcast emails logged into the same unified delivery log",
      "The school's white-label From display name",
    ],
  },
  "Push Notifications": {
    what: "Early-access native apps can register a user's device for Expo push, and supported notifications can deep-link into the relevant screen. Push delivery is best-effort and never blocks the triggering workflow; supported events can also create an in-app bell record for users who have push disabled.",
    capabilities: [
      "Device registration with tap-to-navigate deep links",
      "Push rides alongside in-app bell notifications",
      "Per-user notification preferences and excludes",
      "Best-effort delivery that never blocks the triggering action",
    ],
  },
  "Parent App": {
    ai: true,
    what: "The native Parent App is in early access ahead of store release. Its supported views keep each signed-in guardian scoped to their own children across fees and receipts, attendance history, published results, read-only homework, transport status, PTM slots and certificates. Parent Aira can answer supported child-scoped questions when the school's plan includes it and its channels are enabled.",
    capabilities: [
      "Fees, attendance, results, homework, transport and certificates per child",
      "A My-Children switcher covers younger students below Class 5",
      "Ask Aira about your own child — web, app, WhatsApp auto-reply, or one-tap voice",
      "OTP-claimed onboarding; every read is double-fenced to the parent's own children",
    ],
  },
  "Student App": {
    what: "The native Student App is in early access ahead of store release. Students from Class 5 up can sign in to view their timetable, read-only homework, attendance, published results, moderated class rooms and achievements. Profile changes remain governed through school-reviewed correction requests.",
    capabilities: [
      "Timetable, homework, attendance, results and report cards in one portal",
      "Section-scoped class chat with school-safe rules",
      "Profile correction requests reviewed and approved by the school",
      "Registration-number or mobile sign-in — no school code needed after joining",
    ],
  },
  "Teacher App": {
    ai: true,
    what: "The native Teacher App is in early access ahead of store release. Supported workflows include offline-tolerant attendance, a task inbox with photo proof, My Schedule, Aira when enabled, and best-effort push alerts. Payslips, Form-16 and salary-bank self-service appear only once the HR & Payroll workspace — still rolling out school by school — is enabled for the school.",
    capabilities: [
      "Offline-tolerant attendance marking with durable, idempotent replay",
      "A task inbox with camera and gallery photo-proof",
      "Payslips, Form-16 and salary-bank self-service when HR & Payroll is enabled",
      "The Aira assistant with live-data answers, on the phone",
    ],
  },
  "Notice Board": {
    what: "A digital notice board supports pinning, categories (holiday, fee, exam, event, urgent) and expiry dates that hide stale notices. Publishing can queue delivery to selected audiences over the channels configured for the school, while public notices can also appear on the school website. Channel delivery remains asynchronous and provider-dependent.",
    capabilities: [
      "Pin-to-top, categories and automatic expiry",
      "Audience targeting down to a single class-section",
      "Per-notice fan-out stats: delivered, failed, read",
      "Public notices surface on the school's own website",
    ],
  },
  "Announcements": {
    what: "Publish an announcement to selected audiences such as parents, students, staff, teachers or one class-section, then choose from the channels configured for the school. A dry-run estimates eligible recipients and identity deduplication reduces repeated sends. Actual delivery remains asynchronous and depends on provider, consent and channel policy.",
    capabilities: [
      "Audience tokens including class/section targeting, deduped per family",
      "A live recipient-count preview with masked contact samples",
      "Schedule for later — the worker publishes and fans out on time",
      "Announcement-360: delivery rate, read rate, per-channel statistics",
    ],
  },
  "Event Updates": {
    what: "Event-category announcements can target the relevant audiences, while PTM invites can include slot information and a venue or video link over configured WhatsApp delivery. Reminder results distinguish attempted, provider-reported, opted-out and quiet-mode-skipped recipients; those statuses do not prove that every recipient read the message.",
    capabilities: [
      "Event announcements with scheduling and expiry",
      "PTM invites and reminders per session over WhatsApp",
      "Alumni reunion events with RSVP tracking",
      "Counts separated by attempted, provider-reported, opted-out and quiet-mode-skipped states",
    ],
  },
  "Homework Updates": {
    what: "Published assignments appear as read-only items in the current parent and student portals. From the teacher-managed submissions roster, staff can queue a WhatsApp reminder for eligible non-submitters' guardians, with a per-assignment cooldown, provider configuration, consent and opt-outs applied.",
    capabilities: [
      "Assignments visible in the parent and student portals after the next refresh or sync",
      "One-click non-submitter reminders to guardians",
      "A per-assignment cooldown against repeat nudges",
      "Consent and opt-outs enforced at the send funnel",
    ],
  },
  "Automated Fee Reminders": {
    ai: true,
    what: "Payment nudges across the whole lifecycle: opt-in pre-due reminders at 7, 3 and 0 days (each stage exactly once), manual and bulk reminders from the invoice list, and the AI recovery ladder for overdue cases — every path sharing one safety funnel with a paid-guard, so a settled invoice is never chased. Pay links ride along when a gateway is configured.",
    capabilities: [
      "Pre-due 7/3/0-day nudges — each stage exactly once per invoice",
      "Bulk reminders over selected invoices, with server cooldowns",
      "AI-drafted messages in 11 languages, edited and sent verbatim",
      "A paid-guard everywhere: payment halts every pending reminder",
    ],
  },
  "Attendance Alerts": {
    what: "When configured, saving a register queues absence WhatsApps with a one-per-student-per-day cap, and a scheduled scan can flag cumulative threshold shortfalls monthly. Optional gate-alert attempts can use compatible device punches. Every channel remains subject to consent, opt-outs, provider availability and delivery status.",
    capabilities: [
      "Same-day absence alerts the moment a register saves",
      "Board-threshold shortfall flags, once per student per month",
      "Optional biometric gate entry/exit alerts (default off for privacy)",
      "An opt-in monthly attendance digest for every guardian",
    ],
  },
  "Complaint Management": {
    what: "Two channels, properly separated: a front-office complaints register (open → in-progress → resolved, with a required resolution note) for general feedback, and a private grievance channel where a student or staff member can raise a concern that ONLY admins and the principal see — a student's complaint about a teacher never reaches that teacher.",
    capabilities: [
      "A front-office register with categories and resolution discipline",
      "Private concerns delivered per-admin, never broadcast",
      "Status tracking for the submitter: open → acknowledged → resolved",
      "Anti-spam caps, with the full text in the admin's notification bell",
    ],
  },
  "Chat System": {
    what: "Subject-wise class chat rooms auto-generated from the timetable — a student only sees their own section's rooms, teachers moderate the rooms they teach, and admins get full oversight with an audit trail. School-safe by construction: no phone numbers exposed, no private DMs, per-room mute.",
    capabilities: [
      "Rooms auto-generate from the timetable and teaching assignments",
      "Teacher-only mode, per-room student mute, pin and hide — all audit-logged",
      "PDF notes shared in-room with persona-scoped downloads",
      "No phone numbers exposed and no student-visible member roster",
    ],
  },
  "Circular Management": {
    ai: true,
    what: "Draft a circular from a title and brief with Aira, then have the office edit and approve it before attempting delivery through configured WhatsApp templates or DLT-registered SMS. Categories, pinning, expiry and archives retain the record; provider-reported delivery status is logged separately from the content workflow.",
    capabilities: [
      "AI circular drafting with tone and length control, in 11 languages",
      "Approved-template WhatsApp plus DLT-compliant SMS delivery",
      "Categories, pinning, expiry and an archive lifecycle",
      "Full per-circular delivery statistics",
    ],
  },
  "Birthday Wishes": {
    what: "A scheduled month-and-day lookup can create birthday greetings under the school's display rules. The birthday surface does not show age, birth year or full date of birth, although the underlying SIS still stores the student's date of birth as an authorised school record. Teacher birthday visibility remains scoped to students they teach.",
    capabilities: [
      "Daily scheduled greetings — nothing computed on page load",
      "The birthday surface exposes month and day only, not age, birth year or full DOB",
      "Role-scoped visibility: admins, classmates, and a student's own teachers",
      "One-tap preset wishes, idempotent per sender per day — Feb-29 handled too",
    ],
  },
  "Emergency Alerts": {
    what: "An urgent announcement can queue a broadcast over the school's selected configured channels, while transport SOS can record an incident and create critical staff alerts. A mid-trip vehicle substitution can queue a route-specific parent notice. Each path is asynchronous and subject to provider availability, recipient eligibility and delivery status.",
    capabilities: [
      "Urgent broadcasts across selected configured channels in a single pass",
      "Crew SOS raises an immutable incident and a critical staff alert",
      "Route-parent notice queue on a recorded mid-trip vehicle substitution",
      "In-app bell and best-effort push can be queued together for supported alerts",
    ],
  },
};
